import { Vector2 } from '@babylonjs/core/Maths/math';

import { Planet } from '../Objects/planet';
import { Player } from '../Player/player';
import { minSize } from '../Objects/star';
import { TileMap } from './tileMap';
import { MeshSystem } from '../System/meshSystem';
import { GravityGrid } from '../System/gravityGrid';
import { BlackHole } from '../Objects/blackHole';
import { IAPlayer } from '../Player/iaPlayer';
import { StarCategory, StarCategories, starMapDistance } from '../Objects/star';

export const MAX_LEVEL = 30;

export class LocalMap {

    system: MeshSystem;
    gravityGrid: GravityGrid;
    tileMap: TileMap

    constructor(system: MeshSystem, gravityGrid: GravityGrid, tileMap: TileMap) {
        this.system = system;
        this.gravityGrid = gravityGrid;
        this.tileMap = tileMap;

        setInterval(() => {
            if (this.tileMap.check) {
                this.checkPlayersAbsorbtion();
                this.checkPlanetRessourceMap();
                this.checkIaMap();
            }
        }, 200);
    }

    checkPlayersAbsorbtion() {
        for (const key in this.tileMap.stars) {
            if (this.tileMap.stars.hasOwnProperty(key)) {
                const player: Player = this.tileMap.stars[key];
                if (player.isStarVisible && !player.isBlackHole) {
                    this.checkPlayerAbsorption(player);
                    this.checkPlanetMap(player);
                    this.checkPlayerSize(player);
                    if (player.size < minSize) this.playerDead(player);
                    // if (player.size > MAX_LEVEL) this.playerBlackHole(player);
                }
            }
        }
    }

    playerDead(player: Player) {
        this.tileMap.killPlayer(player);
        if (player.ia) this.removeIa(player);
    }

    playerBlackHole(player: Player) {
        this.tileMap.removeStar(player);
        player.changeToBlackHole(() => {
            this.tileMap.addBlackHole(player);
        });
    }

    // Burning sun cost in size
    checkPlayerSize(player: Player) {
        player.changeSize(-player.size/10000);
    }

    checkPlayerAbsorption(player: Player) {
        // let blackHoleTest = this.checkBlackHoleAbsorption(player);
        // if (!blackHoleTest) this.checkStarAbsorption(player);
        this.checkStarAbsorption(player);
    }

    checkBlackHoleAbsorption(player: Player): boolean {
        let blackHoleTest = '';
        let minDist = 1000000;
        let minBlackHoleField = 1;
        for (const key in this.tileMap.blackHoles) {
            const blackHole: BlackHole = this.tileMap.blackHoles[key];
            let blackHoleField = blackHole.gravityField;
            let dist = Vector2.Distance(blackHole.position, player.position);
            if (dist < blackHoleField) {
                if (minDist > dist) {
                    minDist = dist;
                    minBlackHoleField = blackHoleField;
                    blackHoleTest = blackHole.key;
                }
            }
        }

        if (blackHoleTest) {
            let blackHole: BlackHole = this.tileMap.blackHoles[blackHoleTest]
            player.absorbByBlackHole(blackHole, minDist);
            player.setAbsorber(blackHole, minDist / minBlackHoleField);
            return true;
        } else {
            if (player.blackHoleAbsorber) player.absorbStop();
        }
        return false;
    }

    checkStarAbsorption(player: Player) {
        let closestTarget: Player;
        let closestAbsorber: Player;
        let minTargetDist = 1000000;
        let minAbsorberDist = 1000000;
        let testTarget = '';
        let playerGravityField = player.gravityField;
        for (const key in this.tileMap.stars) {
            const otherplayer: Player = this.tileMap.stars[key];
            if (otherplayer.isStarVisible && otherplayer.key != player.key ) {
                let dist = Vector2.Distance(player.position, otherplayer.position);
                dist /= 1.5; // To make absorb from further possible
                if (player.size > otherplayer.size) {
                    if (minTargetDist > dist) {
                        minTargetDist = dist;
                        closestTarget = otherplayer;
                        if (dist < playerGravityField) {
                            testTarget = otherplayer.key;
                        }
                    }
                } else {
                    if (minAbsorberDist > dist) {
                        minAbsorberDist = dist;
                        closestAbsorber = otherplayer;
                    }
                }
            }

        }

        if (player.ia) player.checkAction(closestTarget, closestAbsorber);

        if (testTarget) {
            player.absorbTarget(closestTarget, minTargetDist);
            closestTarget.setAbsorber(player, minTargetDist / playerGravityField);
            return true;
        } else {
            player.absorbStop();
            return false;
        }

    }

    planetDensity = 200;
    checkPlanetRessourceMap() {
        for (const key in this.tileMap.planets) {
            const planet = this.tileMap.planets[key];
            let dist = Vector2.Distance(planet.position, this.system.center);
            if (dist > this.planetDensity) {
                planet.hide();
                this.tileMap.storagePlanet(planet);
            }
        }
        let newPlanetAvailable = this.tileMap.planetsStorage.length;
        for (let i = 0; i < newPlanetAvailable; i++) {
            this.addNewPlanet();
        }
    }

    checkPlanetMap(player: Player) {
        // add planet while accelerating create bug with planet
        if (player.accelerating) return;
        if (Object.keys(player.satellites).length < player.maxPlanet) {
            for (const key in this.tileMap.planets) {
                const planet: Planet = this.tileMap.planets[key];
                // New check maxPlanet because in the loop it can change
                if (Object.keys(player.satellites).length < player.maxPlanet) {
                    let dist = Vector2.Distance(planet.position, player.position);
                    if (dist < player.gravityField) {
                        this.tileMap.setPlanetWithStar(planet);
                        player.addPlanet(planet);
                    }
                }
            }
        }
    }

    addNewPlanet() {
        let newPlanet = this.tileMap.addPlanet();
        let pos = this.getFreePosition(10, 50, 50);
        newPlanet.setPosition(pos);
        return newPlanet;
    }

    iaLevel = 8;
    setIaLevel(level: number) {
        this.iaLevel = level;
    }

    iaNeeded = starMapDistance / 5;
    // iaNeeded = 2;
    ias: Object = {};
    checkIaMap() {
        let newIaNeeded = Math.round(this.iaNeeded - Object.keys(this.ias).length);
        for (let i = 0; i < newIaNeeded; i++) {
            this.createIa();
        }

        for (const key in this.ias) {
            const ia:IAPlayer = this.ias[key];
            if (ia.isStarOnScreen()) ia.showIA();
            else ia.hideIA();

            if (!ia.isStarOnMap()) {
                ia.dispose();
                this.removeIa(ia);
            }
        }
    }

    getRandomCategory(): StarCategory {
        let l = StarCategories.length;
        let alea = Math.round(Math.random() * l - 0.5);
        return StarCategories[alea];
    }

    createIa() {
        let newIa = new IAPlayer(this.system, this.gravityGrid, this.tileMap);
        let pos = this.getFreePosition(starMapDistance / 2, 25, 5);
        newIa.setPosition(pos);
        let cat = this.getRandomCategory();
        newIa.setCategory(cat, false);
        newIa.setLevel(this.iaLevel);
        this.addIa(newIa);
    }
    
    addIa(ia: IAPlayer) {
        this.ias[ia.key] = ia;
        this.tileMap.addStar(ia);
    }

    removeIa(ia: IAPlayer) {
        this.tileMap.removeStar(ia);
        ia.absorbStop();
        if (ia.target) ia.target.absorbStop();
        delete this.ias[ia.key];
    }

    getFreePosition(gap: number, close: number, step: number): Vector2 {
        let test = true;
        let distProgress = gap;
        while (test) {
            let pos = this.tileMap.getNewRandomPositionFromCenter(this.system.center, distProgress, distProgress);
            let distTest = true;
            for (const key in this.tileMap.stars) {
                let player: Player = this.tileMap.stars[key];
                let dist = Vector2.Distance(pos, player.position);
                if (dist < close) { distTest = false; }
            }
            if (distTest) {
                test = false;
                return pos;
            }
            distProgress += step;
        }
        return new Vector2(0, 0);
    }

    eraseAllIas() {
        for (const key in this.ias) {
            this.ias[key].dispose();
            this.removeIa(this.ias[key]);
        }
        this.ias =  {};
    }
}